import { useEffect, useRef } from "react";
import { X, PlayCircle } from "@phosphor-icons/react";

/**
 * Universal player. Handles:
 *   - Direct video URLs (mp4/webm/ogg)
 *   - YouTube (youtube.com/watch, youtu.be, youtube.com/embed)
 *   - Vimeo (vimeo.com/123, player.vimeo.com)
 *   - No URL → friendly "coming soon" fallback
 */
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}?rel=0&modestbranding=1`;
    if (host.endsWith("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return url;
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
    }
    if (host === "vimeo.com") return `https://player.vimeo.com/video/${u.pathname.slice(1)}`;
    if (host === "player.vimeo.com") return url;
  } catch { /* not a URL */ }
  return null;
}

function isDirectVideo(url) {
  return /\.(mp4|webm|ogg|m4v|mov)(\?|$)/i.test(url || "");
}

export default function VideoPlayer({ open, onClose, content }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !content) return null;
  const url = content.video_url;
  const embed = toEmbedUrl(url);
  const direct = isDirectVideo(url);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="video-player-modal"
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl bg-[#1a120c] rounded-2xl overflow-hidden shadow-2xl"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black text-white flex items-center justify-center"
          data-testid="player-close-btn"
        >
          <X size={18} />
        </button>
        <div className="aspect-video w-full bg-black">
          {embed ? (
            <iframe
              src={embed}
              title={content.title}
              className="w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              data-testid="player-iframe"
            />
          ) : direct ? (
            <video
              src={url}
              controls
              autoPlay
              className="w-full h-full"
              data-testid="player-video"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center px-8 py-12">
              <PlayCircle size={64} weight="duotone" className="text-[#C4A265] mb-4" />
              <h3 className="font-heading text-2xl text-white mb-2">{content.title}</h3>
              <p className="font-body text-sm text-white/70 max-w-md">
                {content.is_live
                  ? "This stream isn't live just yet—we'll start broadcasting at the scheduled time."
                  : "Playback isn't available for this item yet. Check back soon."}
              </p>
            </div>
          )}
        </div>
        <div className="px-5 py-4 bg-[#1a120c] text-white">
          <div className="flex items-center gap-2 mb-1">
            {content.is_live && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-body font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
              </span>
            )}
            <span className="text-[#C4A265] text-[10px] uppercase tracking-wider font-body">{content.category}</span>
          </div>
          <h3 className="font-heading text-xl mb-1">{content.title}</h3>
          <p className="font-body text-xs text-white/60">{content.speaker} · {content.duration}</p>
          {content.description && (
            <p className="font-body text-sm text-white/80 mt-3 leading-relaxed">{content.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}
